// src/pages/Dashboard.js
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import styles from './Dashboard.module.css';

// ─── Free tier upgrade CTA ───────────────────────────────────────────────────

const UPGRADE_OPTIONS = [
  {
    id: 'colleague',
    eyebrow: 'Curious what a colleague would score?',
    title: 'Share the mirror',
    description: 'Invite someone you work with to take the same assessment. Compare results and start a real conversation.',
    cta: 'Invite a colleague',
    style: 'subtle',
    icon: '◎',
  },
  {
    id: 'leader',
    eyebrow: 'The most impactful thing you can do',
    title: 'I lead a team — let\'s do this together',
    description: 'Individual scores are insights. Team scores are a transformation. Upgrade to gather your whole team\'s honest perspective in one place.',
    cta: 'Unlock team assessments',
    style: 'gold',
    icon: '⬡',
  },
  {
    id: 'consultant',
    eyebrow: 'Want expert interpretation?',
    title: 'Get expert eyes on your team',
    description: 'A certified consultant can observe your meetings, enrich your results, and turn data into a clear action plan.',
    cta: 'Talk to a consultant',
    style: 'subtle',
    icon: '◈',
  },
];

function FreeUpgradeCTA() {
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const handleAction = (id) => {
    setSelected(selected === id ? null : id);
    setEmail('');
    setSent(false);
  };

  return (
    <div className={styles.ctaContainer}>
      <div className={styles.ctaGradient} />

      <div className={styles.ctaHeader}>
        <div className={styles.ctaEyebrow}>Ready to go deeper?</div>
        <h3 className={styles.ctaTitle}>
          Your score is just the beginning.<br />
          <em className={styles.ctaTitleAccent}>What will you do with it?</em>
        </h3>
      </div>

      <div className={styles.ctaOptions}>
        {UPGRADE_OPTIONS.map(opt => {
          const isGold = opt.style === 'gold';
          const isOpen = selected === opt.id;

          return (
            <div key={opt.id}>
              <button
                onClick={() => handleAction(opt.id)}
                className={styles.optionBtn}
                style={{
                  background: isGold
                    ? (isOpen ? 'rgba(203,164,58,0.18)' : 'rgba(203,164,58,0.1)')
                    : (isOpen ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.04)'),
                  border: `1.5px solid ${
                    isGold
                      ? (isOpen ? 'rgba(203,164,58,0.6)' : 'rgba(203,164,58,0.3)')
                      : (isOpen ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)')
                  }`,
                }}
                onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = isGold ? 'rgba(203,164,58,0.15)' : 'rgba(255,255,255,0.07)'; }}
                onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = isGold ? 'rgba(203,164,58,0.1)' : 'rgba(255,255,255,0.04)'; }}
              >
                <div
                  className={styles.optionIcon}
                  style={{
                    background: isGold ? 'rgba(203,164,58,0.2)' : 'rgba(255,255,255,0.07)',
                    color: isGold ? 'var(--gold-light)' : 'rgba(255,255,255,0.5)',
                  }}
                >
                  {opt.icon}
                </div>
                <div className={styles.optionContent}>
                  <div
                    className={styles.optionEyebrow}
                    style={{ color: isGold ? 'var(--gold)' : 'rgba(255,255,255,0.35)' }}
                  >
                    {opt.eyebrow}
                  </div>
                  <div
                    className={styles.optionTitle}
                    style={{
                      color: isGold ? 'var(--gold-light)' : 'white',
                      marginBottom: isOpen ? 8 : 0,
                    }}
                  >
                    {opt.title}
                  </div>
                  {isOpen && (
                    <div className={styles.optionDesc}>{opt.description}</div>
                  )}
                  {isOpen && opt.id === 'colleague' && (
                    sent ? (
                      <div className={styles.sentMsg}>✓ Invite link copied — share it with your colleague.</div>
                    ) : (
                      <div className={styles.emailRow}>
                        <input
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          placeholder="colleague@company.com"
                          className={styles.emailInput}
                        />
                        <button
                          className={`btn ${styles.sendBtn}`}
                          onClick={e => { e.stopPropagation(); if (email) setSent(true); }}
                        >
                          Send →
                        </button>
                      </div>
                    )
                  )}
                  {isOpen && opt.id === 'leader' && (
                    <Link to="/pricing" onClick={e => e.stopPropagation()} className={styles.leaderLink}>
                      {opt.cta} →
                    </Link>
                  )}
                  {isOpen && opt.id === 'consultant' && (
                    <Link to="/contact" onClick={e => e.stopPropagation()} className={styles.consultantLink}>
                      {opt.cta} →
                    </Link>
                  )}
                </div>
                <div
                  className={styles.optionChevron}
                  style={{
                    color: isGold ? 'var(--gold)' : 'rgba(255,255,255,0.25)',
                    transform: isOpen ? 'rotate(90deg)' : 'none',
                  }}
                >
                  ›
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.ctaFooter}>
        No commitment required. Teams typically see results within one assessment cycle.
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { currentUser, userProfile, effectiveUid } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [leaderNames, setLeaderNames] = useState({});
  const [standaloneMeetings, setStandaloneMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Admins live in the Admin Panel — /dashboard only serves previewed/impersonated roles
  const isAdmin = userProfile?.role === 'admin';
  useEffect(() => {
    if (isAdmin) navigate('/admin', { replace: true });
  }, [isAdmin, navigate]);

  useEffect(() => { loadStats(); }, [userProfile, effectiveUid]);

  async function loadStats() {
    if (!userProfile) { setLoading(false); return; }
    const role = userProfile.role;

    try {
      if (role === 'team_leader' && userProfile.teamId) {
        const teamSnap = await getDoc(doc(db, 'teams', userProfile.teamId));
        const team = teamSnap.data();
        const responsesSnap = await getDocs(query(collection(db, 'responses'), where('teamId', '==', userProfile.teamId)));
        setStats({ teamName: team?.name, memberCount: team?.memberEmails?.length || 0, responsesCount: responsesSnap.size });
      } else if (role === 'consultant') {
        const myEmail = (currentUser?.email || userProfile?.email || '').toLowerCase();
        const teamsSnap = await getDocs(query(collection(db, 'teams'), where('consultantEmails', 'array-contains', myEmail)));
        const teamList = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setTeams(teamList);
        setStats({});

        // Load standalone meetings (no teamId) created by this consultant
        const standaloneSnap = await getDocs(query(
          collection(db, 'meetings'),
          where('consultantId', '==', currentUser.uid),
          where('teamId', '==', null),
        ));
        setStandaloneMeetings(
          standaloneSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
            .slice(0, 10)
        );

        const emails = [...new Set(teamList.map(t => t.leaderEmail).filter(Boolean))];
        if (emails.length > 0) {
          const usersSnap = await getDocs(query(collection(db, 'users'), where('email', 'in', emails)));
          const nameMap = {};
          usersSnap.docs.forEach(d => {
            const { email, displayName } = d.data();
            if (email) nameMap[email] = displayName || email;
          });
          setLeaderNames(nameMap);
        }
      } else if (role === 'free' || role === 'team_member') {
        const responsesSnap = await getDocs(query(collection(db, 'responses'), where('userId', '==', effectiveUid)));
        setStats({ hasSubmitted: responsesSnap.size > 0, teamId: userProfile.teamId });
      } else if (role === 'admin') {
        const teamsSnap = await getDocs(collection(db, 'teams'));
        const usersSnap = await getDocs(collection(db, 'users'));
        setStats({ teamCount: teamsSnap.size, userCount: usersSnap.size });
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      setStats({});
    } finally {
      setLoading(false);
    }
  }

  const role = userProfile?.role || 'free';
  const name = userProfile?.displayName?.split(' ')[0] || 'there';

  return (
    <div className="fade-up">
      <div className="page-header">
        <h2>Good to see you, {name}.</h2>
        <p>
          {role === 'free'        && 'Complete your individual health assessment to get personalized feedback.'}
          {role === 'team_leader' && 'Manage your team, track assessment progress, and view compiled results.'}
          {role === 'team_member' && 'Your team assessment is waiting. Your responses are anonymous.'}
          {role === 'consultant'  && 'Select a team below to record meetings, submit an assessment, or review results.'}
          {role === 'admin'       && 'Manage teams, users, and platform settings.'}
        </p>
      </div>

      {loading ? (
        <p className={styles.loadingText}>Loading your dashboard...</p>
      ) : (
        <>
          {/* Free user */}
          {role === 'free' && (
            <div>
              <div className={`card ${styles.cardSpaced}`}>
                <div className="card-title">Individual Assessment</div>
                <div className="card-subtitle">6 categories · 24 questions · ~8 min</div>
                {stats?.hasSubmitted ? (
                  <>
                    <div className="alert alert-success">You've completed your assessment!</div>
                    <Link to="/results" className="btn btn-primary">View my results →</Link>
                  </>
                ) : (
                  <Link to="/assessment" className="btn btn-primary">Start assessment →</Link>
                )}
              </div>
              <FreeUpgradeCTA />
            </div>
          )}

          {/* Team leader */}
          {role === 'team_leader' && stats && (
            <>
              <div className={`grid-2 ${styles.gridSpaced}`}>
                <div className="stat-card">
                  <div className="stat-label">Team Members</div>
                  <div className="stat-value">{stats.memberCount}</div>
                  <div className="stat-sub">invited via email</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Responses</div>
                  <div className="stat-value">{stats.responsesCount}</div>
                  <div className="stat-sub">submitted so far</div>
                </div>
              </div>
              <div className="grid-2">
                <div className="card">
                  <div className="card-title">{stats.teamName || 'Your Team'}</div>
                  <div className="card-subtitle">Manage members and track progress</div>
                  <Link to="/team" className="btn btn-primary">Manage team →</Link>
                </div>
                <div className="card">
                  <div className="card-title">Compiled Results</div>
                  <div className="card-subtitle">Available once enough responses are in</div>
                  <Link to="/results" className="btn btn-secondary">View results →</Link>
                </div>
              </div>
            </>
          )}

          {/* Team member */}
          {role === 'team_member' && (
            <div className="grid-2">
              <div className="card">
                <div className="card-title">Team Assessment</div>
                <div className="card-subtitle">Your response is anonymous — be honest!</div>
                {stats?.hasSubmitted ? (
                  <>
                    <div className="alert alert-success">Submitted! Thank you.</div>
                    <Link to="/results" className="btn btn-secondary">View team results →</Link>
                  </>
                ) : (
                  <Link to="/assessment" className="btn btn-primary">Take assessment →</Link>
                )}
              </div>
              <div className="card">
                <div className="card-title">Your Team Results</div>
                <div className="card-subtitle">Compiled results from all team members</div>
                <Link to="/results" className="btn btn-secondary">View results →</Link>
              </div>
            </div>
          )}

          {/* Consultant */}
          {role === 'consultant' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Link to="/meetings/new" className="btn btn-primary">+ New Meeting</Link>
              </div>

              {standaloneMeetings.length > 0 && (
                <div className="card" style={{ marginBottom: 24 }}>
                  <div className="card-title">Standalone Meetings</div>
                  <div className="card-subtitle">Meetings recorded without a linked team</div>
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {standaloneMeetings.map(m => (
                      <button
                        key={m.id}
                        onClick={() => navigate(`/meetings/${m.id}/report`)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, background: 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'var(--transition)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--ink)' }}>{m.name}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginTop: 2 }}>
                            {m.meetingType} · {m.attendees?.length || 0} attendees
                          </div>
                        </div>
                        <span style={{ color: 'var(--ink-muted)', fontSize: '1.2rem' }}>›</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {teams.length === 0 ? (
                <div className={`card ${styles.emptyCard}`}>
                  <div className={styles.emptyIcon}>👥</div>
                  <h3 className={styles.emptyTitle}>No teams yet</h3>
                  <p className={styles.emptyText}>You'll appear here once a team leader invites you as a consultant.</p>
                </div>
              ) : (
                <div className={styles.teamList}>
                  {teams.map(team => (
                    <button
                      key={team.id}
                      onClick={() => navigate(`/meetings/${team.id}`)}
                      className={styles.teamCard}
                    >
                      <div className={styles.teamCardBody}>
                        <div className={styles.teamCardTop}>
                          <div className="avatar">
                            {team.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div className={styles.teamName}>{team.name}</div>
                            <div className={styles.teamMeta}>
                              {team.memberEmails?.length || 0} members{team.leaderEmail && ` · Led by ${leaderNames[team.leaderEmail] || team.leaderEmail}`}
                            </div>
                          </div>
                        </div>
                        <div className={styles.teamCardBadges}>
                          <div className={styles.badgeGroup}>
                            <div className={styles.badgeLabel}>Meetings</div>
                            <span className="badge badge-blue">View</span>
                          </div>
                          <div className={styles.badgeGroup}>
                            <div className={styles.badgeLabel}>Assessment</div>
                            <span className="badge badge-green">View</span>
                          </div>
                          <div className={styles.badgeGroup}>
                            <div className={styles.badgeLabel}>Results</div>
                            <span className="badge badge-purple">View</span>
                          </div>
                        </div>
                      </div>
                      <span className={styles.teamChevron}>›</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Admin */}
          {role === 'admin' && stats && (
            <>
              <div className={`grid-2 ${styles.gridSpaced}`}>
                <div className="stat-card">
                  <div className="stat-label">Total Teams</div>
                  <div className="stat-value">{stats.teamCount}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Users</div>
                  <div className="stat-value">{stats.userCount}</div>
                </div>
              </div>
              <Link to="/admin" className="btn btn-primary">Open Admin Panel →</Link>
            </>
          )}
        </>
      )}
    </div>
  );
}
